import { ApolloError } from '@apollo/client';
import { useMemo } from 'react';
import { useSalesPortalEditableAccountQuery } from '@/experiences/sales-portal/domain/accounts/screens/account-detail/blocs/documents/sales-portal-editable-account.generated';

export interface EditableAccountData {
  accountId: string;
  address: string;
  address2: string;
  name: string;
  note: string;
  restaurantUUID: string;
  routeStop: string;
}

export interface EditableAccountLoaderParams {
  onCompleted?: (data: EditableAccountData) => void;
  restaurantUUID: string;
  supplierUUID: string;
}

export interface EditableAccountLoaderResult {
  data: EditableAccountData | null;
  error: ApolloError | undefined;
  loading: boolean;
  refetch: () => void;
}

/**
 * Loader fetches editable account information
 *
 * @param onCompleted - Callback invoked when data is loaded
 * @param restaurantUUID - UUID of the restaurant
 * @param supplierUUID - UUID of the supplier
 * @returns {@link EditableAccountLoaderResult}
 *
 * @example
 * const { data, loading } = useEditableAccountLoader({
 *   onCompleted: (data) => setFormValues(data),
 *   restaurantUUID: '123',
 *   supplierUUID: '456',
 * });
 */
export const useEditableAccountLoader = ({
  onCompleted,
  restaurantUUID,
  supplierUUID,
}: EditableAccountLoaderParams): EditableAccountLoaderResult => {
  const { data, loading, error, refetch } = useSalesPortalEditableAccountQuery({
    fetchPolicy: 'no-cache',
    onCompleted: (queryData) => {
      const restaurant = queryData?.restaurant;
      const chat = restaurant?.restaurant_chats[0];
      if (restaurant && onCompleted) {
        onCompleted({
          accountId: chat?.restaurant_account_id ?? '',
          address: restaurant.address?.address ?? '',
          address2: restaurant.address?.address2 ?? '',
          name: restaurant.name,
          note: chat?.restaurant_notes ?? '',
          restaurantUUID: restaurant.uuid,
          routeStop: chat?.chat_route_metadata?.name ?? '',
        });
      }
    },
    variables: { restaurantUUID, supplierUUID },
  });

  const restaurant = data?.restaurant;
  const chat = restaurant?.restaurant_chats[0];

  const editableAccountData = useMemo((): EditableAccountData | null => {
    if (!restaurant) {
      return null;
    }
    return {
      accountId: chat?.restaurant_account_id ?? '',
      address: restaurant.address?.address ?? '',
      address2: restaurant.address?.address2 ?? '',
      name: restaurant.name,
      note: chat?.restaurant_notes ?? '',
      restaurantUUID: restaurant.uuid,
      routeStop: chat?.chat_route_metadata?.name ?? '',
    };
  }, [restaurant, chat]);

  return {
    data: editableAccountData,
    error,
    loading,
    refetch,
  };
};
